import "./EditAdminPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import { capitalizeWords } from "../../../shared/utility";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import QRCode from "qrcode";
import {
  deleteAdmin,
  disableAdminTotp,
  editAdmin,
  getAdminMfaStatus,
  initiateAdminTotpSetup,
  verifyAdminTotpSetup,
} from "../../../api/admin/admin";
import { IAdmin, IAdminMfaSetup, IAdminMfaStatus } from "../../../types/api";
import { CognitoUser } from "../../../types/cognito";

interface IeditAdminPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  admin?: IAdmin;
  user: CognitoUser | null;
}

const initialAdminData: IAdmin = {
  member_id: "",
  first_name: "",
  last_name: "",
  email: "",
};

function EditAdminPopup(props: IeditAdminPopupProps) {
  const { admin, user, showPopup, onClose, onConfirm } = props;
  const isEditingSelf =
    user?.attributes["custom:member_id"] === admin?.member_id;

  const [showHandleDeleteAdminPopup, setShowHandleDeleteAdminPopup] =
    useState(false);
  const [adminData, setAdminData] = useState<IAdmin>(initialAdminData);
  const [originalData, setOriginalData] = useState<IAdmin>(initialAdminData);
  const [mfaStatus, setMfaStatus] = useState<IAdminMfaStatus | null>(null);
  const [mfaSetup, setMfaSetup] = useState<IAdminMfaSetup | null>(null);
  const [mfaQrImage, setMfaQrImage] = useState<string>("");
  const [totpCode, setTotpCode] = useState<string>("");
  const [mfaLoading, setMfaLoading] = useState<boolean>(false);

  useEffect(() => {
    if (admin) {
      setAdminData(admin);
      setOriginalData(admin);
    }
  }, [admin, showPopup]);

  useEffect(() => {
    const fetchMfaStatus = async () => {
      if (!showPopup || !admin || !isEditingSelf) {
        setMfaStatus(null);
        setMfaSetup(null);
        setMfaQrImage("");
        setTotpCode("");
        return;
      }

      const accessToken = user?.signInUserSession?.accessToken?.jwtToken;
      if (!accessToken) {
        toast.error("Saknar access token för 2FA.");
        return;
      }

      try {
        setMfaLoading(true);
        const status = await getAdminMfaStatus(admin.member_id, accessToken);
        if (status) {
          setMfaStatus(status);
        }
      } catch (error) {
        toast.error("Kunde inte hämta 2FA-status.");
      } finally {
        setMfaLoading(false);
      }
    };

    fetchMfaStatus();
  }, [showPopup, admin, isEditingSelf, user]);

  useEffect(() => {
    const isDataChanged = () => {
      return Object.keys(originalData).some(
        (key) => adminData[key] !== originalData[key]
      );
    };

    setHasAdminDataChanged(isDataChanged());
  }, [adminData, originalData]);

  const handleDeleteAdmin = async (member_id: string) => {
    try {
      const deleteAdminResponse = await deleteAdmin(member_id);
      if (!deleteAdminResponse) return;

      toast.success(`Admin: ${member_id} borttagen`);
      onConfirm();
      handleClose();
    } catch (error: any) {
      const errorMessage =
        error.message || "An unexpected error occurred. Please try again.";
      toast.error(`Error deleting admin: ${errorMessage}`);
    }
  };
  const [hasAdminDataChanged, setHasAdminDataChanged] = useState(false);
  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const isInput = (element: EventTarget): element is HTMLInputElement =>
      element instanceof HTMLInputElement;

    const { name, value } = e.target;

    let inputValue: string | boolean = value;
    if (isInput(e.target) && e.target.type === "checkbox") {
      inputValue = e.target.checked;
    }
    switch (name) {
      case "first_name":
        break;
      case "last_name":
        break;
      case "email":
        inputValue = value.toLowerCase();
        break;
      default:
        break;
    }

    setAdminData((prevData) => ({
      ...prevData,
      [name]: inputValue,
    }));
  };

  //

  const handleClose = () => {
    setAdminData(initialAdminData);
    setMfaStatus(null);
    setMfaSetup(null);
    setMfaQrImage("");
    setTotpCode("");
    setMfaLoading(false);
    onClose();
  };

  const parseMfaSetupDetails = (setup_uri: string, email: string) => {
    try {
      const url = new URL(setup_uri);
      const issuer = decodeURIComponent(
        url.searchParams.get("issuer") || "KLTK Booking System"
      );
      const label = decodeURIComponent(url.pathname.replace(/^\//, ""));
      const account = label.includes(":")
        ? label.split(":").slice(1).join(":")
        : email;

      return { issuer, account };
    } catch (error) {
      return {
        issuer: "KLTK Booking System",
        account: email,
      };
    }
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      toast.error("Kunde inte kopiera texten");
    }
  };

  const getAccessToken = () => {
    const accessToken = user?.signInUserSession?.accessToken?.jwtToken;
    if (!accessToken) {
      throw new Error("Saknar access token");
    }
    return accessToken;
  };

  const handleInitiateMfaSetup = async () => {
    if (!admin) return;

    try {
      setMfaLoading(true);
      const response = await initiateAdminTotpSetup(
        admin.member_id,
        getAccessToken()
      );

      if (!response) return;

      const qrDataUrl = await QRCode.toDataURL(response.setup_uri, {
        margin: 1,
        width: 220,
      });

      setMfaSetup(response);
      setMfaQrImage(qrDataUrl);
      setTotpCode("");
      toast.success("2FA-initiering klar. Ange koden från din app.");
    } catch (error: any) {
      toast.error(error?.message || "Kunde inte initiera 2FA.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfaSetup = async () => {
    if (!admin) return;
    if (!/^\d{6}$/.test(totpCode)) {
      toast.error("Ange en 6-siffrig kod.");
      return;
    }

    try {
      setMfaLoading(true);
      const response = await verifyAdminTotpSetup(
        admin.member_id,
        getAccessToken(),
        totpCode
      );

      if (!response) return;

      setMfaStatus(response);
      setMfaSetup(null);
      setMfaQrImage("");
      setTotpCode("");
      toast.success("2FA är aktiverad.");
    } catch (error: any) {
      toast.error(error?.message || "Kunde inte verifiera 2FA-kod.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!admin) return;
    if (!/^\d{6}$/.test(totpCode)) {
      toast.error("Ange en 6-siffrig kod för att stänga av 2FA.");
      return;
    }

    try {
      setMfaLoading(true);
      const response = await disableAdminTotp(
        admin.member_id,
        getAccessToken(),
        totpCode
      );

      if (!response) return;

      setMfaStatus(response);
      setMfaSetup(null);
      setMfaQrImage("");
      setTotpCode("");
      toast.success("2FA är avstängd.");
    } catch (error: any) {
      toast.error(error?.message || "Kunde inte stänga av 2FA.");
    } finally {
      setMfaLoading(false);
    }
  };
  const handleEditAdmin = async () => {
    try {
      const adminToEdit = {
        first_name: adminData.first_name,
        last_name: adminData.last_name,
        email: adminData.email,
      };
      const old_email = originalData.email;
      const member_id = adminData.member_id;

      const response = await editAdmin(member_id, adminToEdit, old_email);

      if (response && !("message" in response)) {
        toast.success(`Admin uppdaterad: ${adminToEdit.first_name}`);
        handleClose();
        onConfirm();
      }
    } catch (error: any) {
      toast.error(error);
    }
  };
  if (admin === undefined) return null;

  return (
    <Popup
      showPopup={showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Redigera {admin.first_name + " " + admin.member_id}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleEditAdmin();
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="firstName" className="text-m">
                Förnamn*
              </label>
              <input
                className="on-transparent text-m"
                type="text"
                id="firstName"
                name="first_name"
                required
                placeholder="Förnamn"
                value={capitalizeWords(adminData.first_name)}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="lastName" className="text-m">
                Efternamn*
              </label>
              <input
                className="on-transparent text-m"
                type="text"
                id="lastName"
                required
                name="last_name"
                placeholder="Efternamn"
                value={capitalizeWords(adminData.last_name)}
                onChange={(e) => handleInputChange(e)}
              />
            </div>

            <div className="general-popup-form-field">
              <label htmlFor="email" className="text-m">
                E-postadress*
              </label>
              <input
                className="on-transparent text-m"
                type="text"
                name="email"
                id="email"
                required
                placeholder="Ange E-postadress"
                value={adminData.email}
                onChange={(e) => handleInputChange(e)}
              />
            </div>

            {isEditingSelf && (
              <>
                <div className="general-popup-form-group-label">
                  <span className="text-s secondary-text">Tvåfaktorsautentisering</span>
                </div>

                <div className="general-popup-form-field">
                  <label className="text-m">Status</label>
                  <span className="text-m">
                    {mfaLoading
                      ? "Hämtar..."
                      : mfaStatus?.enabled
                      ? "Aktiverad"
                      : "Inte aktiverad"}
                  </span>
                </div>

                {!mfaStatus?.enabled && (
                  <>
                    <div className="general-popup-form-field">
                      <button
                        type="button"
                        className="text-m"
                        onClick={handleInitiateMfaSetup}
                        disabled={mfaLoading}
                      >
                        Initiera 2FA (TOTP)
                      </button>
                    </div>

                    {mfaQrImage && (
                      <>
                        <div className="general-popup-form-field">
                          <label className="text-m">Skanna QR-kod i din autentiseringsapp</label>
                          <div>
                            <img src={mfaQrImage} alt="2FA QR code" />
                          </div>
                        </div>

                        {mfaSetup && (
                          <>
                            <div className="general-popup-form-field">
                              <label className="text-m">Kan du inte skanna QR-koden?</label>
                              <span className="text-s secondary-text">
                                Ange uppgifterna manuellt i din autentiseringsapp.
                              </span>
                            </div>

                            <div className="general-popup-form-field">
                              <label className="text-m">Secret key (manuell)</label>
                              <input
                                className="on-transparent text-m"
                                type="text"
                                value={mfaSetup.secret_code}
                                readOnly
                              />
                              <button
                                type="button"
                                className="text-s"
                                onClick={() =>
                                  copyToClipboard(
                                    mfaSetup.secret_code,
                                    "Secret key kopierad"
                                  )
                                }
                              >
                                Kopiera secret key
                              </button>
                            </div>

                            <div className="general-popup-form-field">
                              <label className="text-m">Konto</label>
                              <input
                                className="on-transparent text-m"
                                type="text"
                                value={parseMfaSetupDetails(mfaSetup.setup_uri, mfaSetup.email).account}
                                readOnly
                              />
                            </div>

                            <div className="general-popup-form-field">
                              <label className="text-m">Utgivare (Issuer)</label>
                              <input
                                className="on-transparent text-m"
                                type="text"
                                value={parseMfaSetupDetails(mfaSetup.setup_uri, mfaSetup.email).issuer}
                                readOnly
                              />
                            </div>
                          </>
                        )}

                        <div className="general-popup-form-field">
                          <label htmlFor="totp-code" className="text-m">
                            Verifieringskod (6 siffror)
                          </label>
                          <input
                            className="on-transparent text-m"
                            id="totp-code"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={totpCode}
                            onChange={(e) =>
                              setTotpCode(e.target.value.replace(/\D/g, ""))
                            }
                            placeholder="123456"
                          />
                        </div>

                        <div className="general-popup-form-field">
                          <button
                            type="button"
                            className="text-m"
                            onClick={handleVerifyMfaSetup}
                            disabled={mfaLoading}
                          >
                            Verifiera och aktivera 2FA
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {mfaStatus?.enabled && (
                  <>
                    <div className="general-popup-form-field">
                      <label htmlFor="disable-totp-code" className="text-m">
                        Kod för att stänga av 2FA
                      </label>
                      <input
                        className="on-transparent text-m"
                        id="disable-totp-code"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={totpCode}
                        onChange={(e) =>
                          setTotpCode(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="123456"
                      />
                    </div>

                    <div className="general-popup-form-field">
                      <button
                        type="button"
                        className="deleteButton text-m"
                        onClick={handleDisableMfa}
                        disabled={mfaLoading}
                      >
                        Stäng av 2FA
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="general-popup-bottom-buttons-wrapper">
            {user !== null &&
              user?.attributes["custom:member_id"] !== admin?.member_id && (
                <button
                  className="deleteButton text-m"
                  type="button"
                  onClick={() => setShowHandleDeleteAdminPopup(true)}
                >
                  Radera admin permanent
                </button>
              )}

            <button
              type="submit"
              className="text-m"
              disabled={!hasAdminDataChanged}
            >
              Redigera admin
            </button>
          </div>
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showHandleDeleteAdminPopup}
        onClose={() => setShowHandleDeleteAdminPopup(false)}
        onConfirm={() => handleDeleteAdmin(admin?.member_id || "")}
        questionText={`Är du säker på att radera ${admin?.member_id}?`}
        confirmText="Radera admin"
        denyText="Nej"
      />
    </Popup>
  );
}

export default EditAdminPopup;
